import { Box, Stack } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePreviousDistinct } from 'react-use';
import { makeStyles } from 'tss-react/mui';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { AlertTriangleFilledIcon } from '@/components/icons/AlertTriangleFilledIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { HouseholdChatToggleButton } from '@/modules/household/chat/components/HouseholdChatToggleButton';
import { PrivateHouseholdIndicator } from '@/modules/household/components/PrivateHouseholdIndicator';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { NotificationContainer } from '@/modules/notifications/NotificationContainer/NotificationContainer';
import { useMostRecentlyViewedHouseholds } from '@/modules/user/hooks/useMostRecentlyViewedHouseholds';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { COLORS } from '@/styles/tokens/colors';

import { AppBar, AppBarProps } from './AppBar';
import { APPBAR_HEIGHT } from './AppBar.constants';
import { AppBarMenuItems } from './AppBarMenuItems';
import { TrialBanner } from './TrialBanner';

const useStyles = makeStyles()((theme) => ({
  appBar: {
    height: APPBAR_HEIGHT,
    minHeight: APPBAR_HEIGHT,
  },
  toolBar: {
    backgroundColor: COLORS.PRIMITIVES.WHITE,
    borderBottom: `1px solid ${COLORS.GRAY[200]}`,
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
}));

export function ClientDetailsAppBar(props: AppBarProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const enhanced = useFeatureFlag('enhanced_household_onboarding_flow');

  const {
    householdId,
    displayName: contextDisplayName,
    isPrivateHousehold,
  } = useHouseholdDetailsContext();
  const prevDisplayName = usePreviousDistinct(contextDisplayName);
  const displayName = useMemo(() => {
    if (!contextDisplayName) return prevDisplayName;

    return contextDisplayName;
  }, [contextDisplayName, prevDisplayName]);

  const { mostRecentlyViewedHouseholds, switcherHouseholds } =
    useMostRecentlyViewedHouseholds(
      householdId
        ? {
            id: householdId,
            displayName: displayName ?? '',
            private: isPrivateHousehold,
          }
        : undefined
    );
  const activeHouseholdIsPrivate = mostRecentlyViewedHouseholds.find(
    ({ id }) => id === householdId
  )?.private;

  const menuItems = useMemo(() => {
    const households = switcherHouseholds.map(
      (
        { id, displayName, private: isPrivate, hasPendingValidationQuestions },
        idx
      ) => {
        return (
          <MenuItem
            key={id}
            selected={enhanced && id === householdId}
            iconAfter={
              enhanced && hasPendingValidationQuestions ? (
                <AlertTriangleFilledIcon
                  size={20}
                  sx={{
                    color: COLORS.FUNCTIONAL.WARNING[500],
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'visible',
                  }}
                  aria-label="Validation questions required"
                />
              ) : undefined
            }
            muiMenuItemProps={{
              divider: idx === switcherHouseholds.length - 1,
            }}
            typographyProps={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
            label={displayName}
            endAdornment={isPrivate ? <PrivateHouseholdIndicator /> : undefined}
            onClick={() =>
              navigate(
                getCompletePathFromRouteKey(
                  ROUTE_KEYS.HOUSEHOLD_DETAILS_OVERVIEW,
                  {
                    householdId: id,
                  }
                )
              )
            }
          />
        );
      }
    );

    return (
      <>
        {households}
        <MenuItem
          key="back"
          label="Back to all clients"
          onClick={() =>
            navigate(
              getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLDS_LIST, {})
            )
          }
        />
      </>
    );
  }, [householdId, switcherHouseholds, navigate, enhanced]);

  return (
    <AppBar
      classes={{
        appBar: classes.appBar,
        toolBar: classes.toolBar,
      }}
      showLogomark={false}
      {...props}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {displayName && (
          <ButtonWithPopover
            size="xs"
            variant="secondary"
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <span>{displayName}</span>
                {activeHouseholdIsPrivate && (
                  <PrivateHouseholdIndicator focusable={false} />
                )}
              </Stack>
            }
            popperVariant="menuBelow"
            contentBoxSx={{ pointerEvents: 'auto' }}
          >
            <Box>{menuItems}</Box>
          </ButtonWithPopover>
        )}

        <NotificationContainer />
      </Stack>
      <Box flexGrow={1} />
      <TrialBanner />
      <HouseholdChatToggleButton />
      <AppBarMenuItems disableActiveHighlight appBarTheme="light" />
    </AppBar>
  );
}
